import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Store,
  ShoppingCart,
  Zap,
  Shield,
  BarChart3,
  ArrowRight,
  Check,
  ChevronRight,
  Menu,
  X,
  FileText,
  ChevronsUpDown,
  ExternalLink,
  MonitorCheck,
  Monitor
} from "lucide-react";
import ResellerRequestModal from "@/components/common/ResellerRequestModal";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { getImageUrl } from "@/utils/imageUtils";
import { CollectionCollage } from "@/components/store";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Use axios without credentials for public endpoints
const publicAxios = axios.create({
  withCredentials: false
});

import { usePlatformContext } from "@/context/PlatformContext";

export default function LandingPage() {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { platformInfo } = usePlatformContext();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: Store,
      title: "Become an Authorized Reseller",
      description: "Partner with Infinity Funeral Supply to offer premium cremation products to your community."
    },
    {
      icon: Zap,
      title: "Flexible Pricing",
      description: "Set your own margins with customizable pricing while we handle inventory and shipping."
    },
    {
      icon: Shield,
      title: "Your Brand Identity",
      description: "Customize your store with your logo, colors, and choose from elegant theme presets."
    },
    {
      icon: BarChart3,
      title: "Earn Commissions",
      description: "Track your earnings in real-time and receive payouts for every sale you make."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans animate-fade-in">
      {/* Navigation */}
      <Header />



      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-[#fdfbf7]">
        <div className="max-w-7xl mx-auto relative px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-800 text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Authorized Reseller Program
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6">
                Partner with<br />
                <span className="bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">eDisplayroom</span>
              </h1>
              <p className="text-lg text-slate-600 mb-6 max-w-[480px] leading-relaxed">
                Join the eDisplayroom reseller network and offer premium cremation urns,
                memorial jewelry, and keepsakes to families in your community. We handle inventory and shipping.
              </p>
              <div className="flex flex-col gap-2 items-start mt-4">
                <Button
                  size="lg"
                  className="bg-[#0f172a] hover:bg-black text-white rounded-full px-8 h-12 text-base font-bold shadow-md w-fit"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Become a Reseller →
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full px-5 h-9 border-[#af8c3a] hover:bg-[#af8c3a]/5 text-[#af8c3a] gap-2 font-bold text-sm w-fit"
                  onClick={() => window.open('https://infinityurns.edisplayroom.com/', '_blank')}
                >
                  <Monitor className="w-4 h-4" />
                  Demo Store
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in hidden lg:block">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-100 to-indigo-100 rounded-3xl blur-2xl opacity-40"></div>
              <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-slate-900 aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/sDwjwyBr5vc?rel=0"
                  title="eDisplayroom - Reseller Program"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Trusted by Funeral Homes
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Partner With Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to serve families with dignity and compassion.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const colors = [
                { bg: "bg-gradient-to-br from-amber-50 to-amber-100", icon: "bg-amber-500", text: "text-amber-600" },
                { bg: "bg-gradient-to-br from-indigo-50 to-indigo-100", icon: "bg-indigo-500", text: "text-indigo-600" },
                { bg: "bg-gradient-to-br from-emerald-50 to-emerald-100", icon: "bg-emerald-500", text: "text-emerald-600" },
                { bg: "bg-gradient-to-br from-rose-50 to-rose-100", icon: "bg-rose-500", text: "text-rose-600" }
              ];
              const color = colors[index % colors.length];
              return (
                <div
                  key={index}
                  className={`${color.bg} p-6 rounded-2xl border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-slide-up`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`w-12 h-12 ${color.icon} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
              <Check className="w-4 h-4" />
              Simple Process
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Become a Reseller in 3 Steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Apply to Join",
                description: "Submit your application with your business details. We review all funeral homes and memorial service providers.",
                color: "from-amber-500 to-amber-600"
              },
              {
                step: "02",
                title: "Get Approved",
                description: "Once approved, we set up your personalized store with your chosen categories and theme.",
                color: "from-indigo-500 to-indigo-600"
              },
              {
                step: "03",
                title: "Start Earning",
                description: "Add your branding, set your prices, and earn commissions on every sale. We handle fulfillment.",
                color: "from-emerald-500 to-emerald-600"
              }
            ].map((item, index) => (
              <div key={index} className="relative bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl border border-slate-100 shadow-lg">
                <div className={`text-5xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-4`}>
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.description}
                </p>
                {index < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 w-8 h-8 text-amber-400" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full text-amber-300 text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Start Today
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Join Our Reseller Network?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Partner with {platformInfo.platform_name || 'eDisplayroom'} and help families honor their loved ones with dignity.
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-full px-8 text-lg h-14 shadow-xl shadow-amber-500/30"
            onClick={() => setIsDialogOpen(true)}
          >
            Apply Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer onOpenResellerDialog={() => setIsDialogOpen(true)} />

      {/* Unified Reseller Request Modal */}
      <ResellerRequestModal open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </div>
  );
}
